import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, firstValueFrom, iif, Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { DOWNLOAD_DONE_VISIBILITY_DURATION, DownloadService } from './abstract-services/download.service';
import { ExperimentalFeaturesService } from './experimental-features.service';

const EVENTS_TO_TRIGGER_CD = [HttpEventType.Sent, HttpEventType.Response];

@Injectable({ providedIn: 'root' })
export class FileDownloadService extends DownloadService {
    private readonly filesBaseUrl = '/api/documents/files/';
    constructor(
        private http: HttpClient,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {
        super();
    }

    /**
     * returned observable emits change detection relevant events in case parent components are OnPush
     * first one is starting event
     * second one is the download done event that sets the progress 'isDone' to true
     * (enables possibility of a temporary state to show a e.g. checkmark icon)
     * third one resets the progress state to go back to the initial state
     * (show initial download icon)
     */
    downloadFile(url: string, fileName?: string): Observable<number | null | void> {
        // use last part of url ending as a default fileName
        const _fileName = fileName || url.split('/').pop();
        return this.http
            .get(url, {
                responseType: 'blob',
                reportProgress: true,
                observe: 'events',
            })
            .pipe(
                tap((event: HttpEvent<Blob>): void => this.handleEvent(event, _fileName)),
                filter((event: HttpEvent<Blob>): boolean => EVENTS_TO_TRIGGER_CD.includes(event.type)),
                switchMap(
                    (event: HttpEvent<Blob>): Observable<number | null> =>
                        iif((): boolean => event.type === HttpEventType.Sent, of(null), timer(0, DOWNLOAD_DONE_VISIBILITY_DURATION)),
                ),
                take(3),
                catchError((e: unknown): Observable<never> => {
                    super.error();
                    return throwError(e);
                }),
            );
    }

    getShortLivedDownloadUrl$(url: string): Observable<string> {
        const options = {
            headers: new HttpHeaders({
                Accept: 'text/html, application/xhtml+xml, */*',
                'Content-Type': 'text/plain; charset=utf-8',
            }),
            // eslint-disable-next-line @typescript-eslint/prefer-as-const
            responseType: 'text' as 'text',
        };

        return this.http.post(url, {}, options).pipe(
            tap(response => {
                console.log('Received response from short-lived URL request:', response);
            }),
            catchError((error: unknown): Observable<never> => {
                console.error('Error while fetching short-lived URL:', error);
                return EMPTY;
            }),
        );
    }

    /**
     * this approach enables native browser download progress
     */
    async nativeDownload(url: string, name?: string, fileId?: string): Promise<void> {
        if (this.experimentalFeaturesService.isMEDProjectActive === true && fileId != null) {
            try {
                const downloadUrl = await firstValueFrom(
                    this.http.post(`${this.filesBaseUrl}download?fileName=${name}&fileId=${fileId}`, null, {
                        responseType: 'text',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    }),
                );

                // Now that we have the URL, we can proceed with creating the link and triggering the download
                const link = document.createElement('a');
                let url = `${this.filesBaseUrl}${downloadUrl}`.replace('"', '').replace('"', '');
                if (url.endsWith('%22')) {
                    url = url.substring(0, url.length - 3);
                }

                link.href = url; // Assuming response is the download URL
                if (name) {
                    link.download = name;
                }
                document.body.appendChild(link); // Add link to the document to ensure it works in Firefox
                link.click();
                document.body.removeChild(link); // Remove the link after triggering the download
            } catch (error) {
                console.error('Error downloading the file:', error);
            }
        } else {
            const a = document.createElement('a');
            a.href = url;
            if (name) {
                a.download = name;
            }
            a.click();
        }
    }

    async nativeDownloadFromStream(fileIds: string[], name?: string): Promise<void> {
        try {
            const downloadUrl = await firstValueFrom(
                this.http.post(`${this.filesBaseUrl}zipdownload?fileName=${name}`, `"${fileIds.join(',')}"`, {
                    responseType: 'text',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                }),
            );

            // Now that we have the URL, we can proceed with creating the link and triggering the download
            const link = document.createElement('a');
            let url = `${this.filesBaseUrl}${downloadUrl}`.replace('"', '').replace('"', '');
            if (url.endsWith('%22')) {
                url = url.substring(0, url.length - 3);
            }

            link.href = url; // Assuming response is the download URL
            if (name) {
                link.download = name;
            }
            document.body.appendChild(link); // Add link to the document to ensure it works in Firefox
            link.click();
            document.body.removeChild(link); // Remove the link after triggering the download
        } catch (error) {
            console.error('Error downloading the file:', error);
        }
    }
}
